import { useContext, useState } from "react";

import { CartContext } from "../contexts/CartContext";
import FoundryLink from "../components/FoundryLink";
import { Rotate as Hamburger } from "hamburger-react";
import LogoWhite from "../public/img/logo_black.svg";
import { NavigationContext } from "../hooks/contexts/NavigationContext";
import Routes from "../routes";
import Settings from "../settings";
import ShoppingCartSvg from "../svg/shopping-cart.svg";
import UserSvg from "../svg/user.svg";
import styles from "./Navigation.module.scss";

export default function Navigation({ activeComponentName }) {
  const [isOpen, setOpen] = useState(false);
  const { cartDetails } = useContext(CartContext);
  const cartCount = cartDetails?.itemCount();
  const { navState } = useContext(NavigationContext);

  let mobileStyle = {
    position: "fixed",
    transform: isOpen ? "none" : "translateX(100%)",
    height: "100%",
    background: "var(--color-secondary)",
  };

  const NavLink = (
    componentName,
    href,
    indicator = false,
    displayComponent?,
    classNames?
  ) => {
    let classes = "";
    if (displayComponent) {
      classes =
        styles.link +
        " transition-color duration-200 md:bg-neutral-500-hover md:p-1 md:rounded";
    } else {
      classes = styles.link;
      if (activeComponentName?.includes(componentName)) {
        classes += ` ${styles.active}`;
      }
    }
    const baseLink = (
      <div className={classes}>
        <FoundryLink
          href={href}
          onClick={(e) => {
            setOpen(false);
          }}
        >
          {displayComponent || componentName}
        </FoundryLink>
      </div>
    );

    if (indicator && cartCount) {
      return (
        <div
          className={`relative inline-flex ${classNames} items-center pointer-events-all`}
        >
          <span className="md:absolute right-0 top-0 md:-translate-y-1/2 md:translate-x-4 bg-mint p-1 rounded-full text-xs w-6 h-6 flex flex-row items-center justify-center">
            {cartCount.toString()}
          </span>
          {baseLink}
        </div>
      );
    }

    return baseLink;
  };

  const NavVersion = (classNames, style) => {
    let classes =
      "flex flex-col md:flex-row container-nav w-full px-14 py-6 pointer-events-none " +
      classNames;
    classNames;

    return (
      <div className={classes} style={style}>
        <div className="logo flex flex-col items-end md:items-center md:justify-center overflow-y-hidden pointer-events-all">
          <FoundryLink href="/" onClick={() => setOpen(false)}>
            <LogoWhite />
          </FoundryLink>
        </div>
        <div className="pointer-events-all self-end">
          <div id="nav-area" className="h-full">
            <div
              id="nav-box"
              className="flex flex-col md:flex-row items-end md:items-center md:justify-center h-full gap-4 md:gap-8"
            >
              {process.env.NEXT_PUBLIC_ENABLE_FOUNDRY &&
                NavLink("Fonts", Routes.fonts.index)}
              {NavLink("Shop", Routes.shop.index)}
              {NavLink("Print", Routes.print)}
              {NavLink("About", Routes.about)}
              <div className="flex flex-row gap-2">
                {NavLink(
                  "AccountIndex",
                  Routes.account.index,
                  false,
                  <>
                    <UserSvg className="hidden md:block"></UserSvg>
                    <span className="block md:hidden">Account</span>
                  </>
                )}
                {cartCount && cartCount > 0 ? (
                  NavLink(
                    "CartIndex",
                    Routes.cart.index,
                    true,
                    <ShoppingCartSvg></ShoppingCartSvg>,
                    "hidden md:block"
                  )
                ) : (
                  <></>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <nav
      className={`absolute w-full max-w-full top-0 pointer-events-${navState.showTitle ? "all" : "none"
        }`}
      style={{ zIndex: 120 }}
    >
      <div
        className={`md:hidden fixed flex flex-row justify-between ${navState.bgColor
          ? `bg-${navState.bgColor}-20-transparent backdrop-blur-sm`
          : ""
          } items-center w-full px-2 gap-2 overflow-hidden`}
        style={{ zIndex: 100 }}
      >
        <div className="flex flex-row items-center gap-4">
          <div className="logo flex items-center overflow-y-hidden pointer-events-all p-4">
            <FoundryLink href="/" onClick={() => setOpen(false)}>
              <LogoWhite style={{ width: 32, height: 32 }} />
            </FoundryLink>
          </div>
          {navState.title && (
            <span
              className="flex flex-row gap-4 pointer-events-all"
              style={{
                borderRadius: "5px",
                transition: "all 0.5s",
                transform: navState.showTitle
                  ? "translateY(0)"
                  : "translateY(130%)",
              }}
            >
              {navState.title}
            </span>
          )}
        </div>
        <div className="flex flex-row gap-2">
          {cartCount && cartCount > 0 ? (
            NavLink(
              "Cart",
              Routes.cart.index,
              true,
              <ShoppingCartSvg></ShoppingCartSvg>
            )
          ) : (
            <></>
          )}
          <div className="pointer-events-all">
            <Hamburger
              size={18}
              color={isOpen ? "var(--color-primary)" : "var(--color-primary)"}
              toggled={isOpen}
              toggle={setOpen}
            />
          </div>
        </div>
      </div>
      <div className="w-full flex justify-center">
        {NavVersion("md:hidden h-full justify-center gap-8 z-10", mobileStyle)}
        {NavVersion(
          `hidden md:flex justify-between ${Settings.isPaperBg(activeComponentName) &&
            Settings.useNavBg(activeComponentName)
            ? "masked-lr"
            : ""
          }`,
          {}
        )}
      </div>
      <div className="mx-4">
        <hr className="hidden md:block container-nav w-full border-t-2 border-black my-0" />
      </div>
    </nav>
  );
}
